import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

type LiningType = 'standard' | 'blackout' | 'sheer' | 'none';
type OperatingSystem = 'manual' | 'somfy_standard' | 'rollease';

interface State {
  widthInches: string;
  heightInches: string;
  mountType: 'inside' | 'outside' | '';
  spaceAboveWindowInches: string;
  fabricWidthInches: string;
  patternRepeatInches: string;
  fabricDescription: string;
  liningType: LiningType;
  bumpInterlining: boolean;
  trimAlongBottom: boolean;
  trimDescription: string;
  operatingSystem: OperatingSystem;
}

const blank: State = {
  widthInches: '',
  heightInches: '',
  mountType: '',
  spaceAboveWindowInches: '',
  fabricWidthInches: '54',
  patternRepeatInches: '',
  fabricDescription: '',
  liningType: 'standard',
  bumpInterlining: false,
  trimAlongBottom: false,
  trimDescription: '',
  operatingSystem: 'manual',
};

function stateFromInitialValues(iv: ObjectTypeFormProps['initialValues']): State {
  if (!iv) return blank;
  return {
    widthInches: iv.widthInches != null ? String(iv.widthInches) : '',
    heightInches: iv.heightInches != null ? String(iv.heightInches) : '',
    mountType: (iv.mountType as State['mountType']) || '',
    spaceAboveWindowInches: iv.spaceAboveWindowInches != null ? String(iv.spaceAboveWindowInches) : '',
    fabricWidthInches: iv.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54',
    patternRepeatInches: iv.patternRepeatInches != null ? String(iv.patternRepeatInches) : '',
    fabricDescription: iv.fabricDescription || '',
    liningType: (iv.liningType as LiningType) || 'standard',
    bumpInterlining: iv.bumpInterlining ?? false,
    trimAlongBottom: iv.trimPlacement === 'bottom',
    trimDescription: iv.trimDescription || '',
    operatingSystem: (iv.operatingSystem as OperatingSystem) || 'manual',
  };
}

const BalloonShadeForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>(({ disabled, initialValues }, ref) => {
  const { user } = useAuth();
  const [state, setState] = useState<State>(() => stateFromInitialValues(initialValues));
  const s = (patch: Partial<State>) => setState(prev => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!state.widthInches || !state.heightInches || !state.mountType) return null;
      const w = parseFloat(state.widthInches);
      const h = parseFloat(state.heightInches);
      if (!w || !h) return null;

      return {
        widthInches: w,
        heightInches: h,
        mountType: state.mountType,
        // Only relevant for Outside Mount — see the work-order Finished
        // Length output (Finished Length = Length + Space Above Window).
        spaceAboveWindowInches: state.mountType === 'outside' && state.spaceAboveWindowInches
          ? parseFloat(state.spaceAboveWindowInches) : null,
        fabricWidthInches: state.fabricWidthInches ? parseFloat(state.fabricWidthInches) : 54,
        patternRepeatInches: state.patternRepeatInches ? parseFloat(state.patternRepeatInches) : null,
        fabricDescription: state.fabricDescription.trim() || null,
        liningType: state.liningType,
        bumpInterlining: state.bumpInterlining,
        trimPlacement: state.trimAlongBottom ? 'bottom' : 'none',
        trimDescription: state.trimDescription.trim() || null,
        operatingSystem: state.operatingSystem,
      };
    },
  }));

  return (
    <div style={{ marginTop: 16 }}>
      {/* Dimensions + mount */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ width: 120 }}>
          <label>Width (in) *</label>
          <input type="number" min="0" step="0.25" className="input" value={state.widthInches}
            onChange={e => s({ widthInches: e.target.value })} disabled={disabled} placeholder="0" />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Length (in) *</label>
          <input type="number" min="0" step="0.25" className="input" value={state.heightInches}
            onChange={e => s({ heightInches: e.target.value })} disabled={disabled} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Mount Type *</label>
          <select className="input" value={state.mountType}
            onChange={e => s({ mountType: e.target.value as 'inside' | 'outside' })} disabled={disabled}>
            <option value="">Select...</option>
            <option value="inside">Inside Mount (IM)</option>
            <option value="outside">Outside Mount (OM)</option>
          </select>
        </div>
        {state.mountType === 'outside' && (
          <div className="form-group" style={{ width: 160 }}>
            <label>Space Above Window (in)</label>
            <input type="number" min="0" step="0.25" className="input" value={state.spaceAboveWindowInches}
              onChange={e => s({ spaceAboveWindowInches: e.target.value })} disabled={disabled} placeholder="0" />
          </div>
        )}
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="1" step="0.5" className="input" value={state.fabricWidthInches}
            onChange={e => s({ fabricWidthInches: e.target.value })} disabled={disabled} placeholder="54" />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.patternRepeatInches}
            onChange={e => s({ patternRepeatInches: e.target.value })} disabled={disabled} placeholder="None" />
        </div>
      </div>

      <div className="form-group">
        <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
        <InventorySearchSelect
          items={fabricItems}
          value={state.fabricDescription}
          onChange={(name, item) => s({
            fabricDescription: name,
            fabricWidthInches: item?.widthInches ? String(item.widthInches) : state.fabricWidthInches,
          })}
          placeholder="Search inventory or type fabric description..."
          disabled={disabled}
        />
      </div>

      {/* Lining */}
      <div className="form-group">
        <label>Lining</label>
        <select className="input" value={state.liningType}
          onChange={e => s({ liningType: e.target.value as LiningType })} disabled={disabled}>
          <option value="standard">Regular Lining</option>
          <option value="blackout">Blackout Lining</option>
          <option value="sheer">Sheer Lining</option>
          <option value="none">None</option>
        </select>
      </div>

      {/* Checkboxes + operating system */}
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div className="form-group">
          <label>Flannel Interlining</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
            <input type="checkbox" checked={state.bumpInterlining}
              onChange={e => s({ bumpInterlining: e.target.checked })} disabled={disabled} />
            Add flannel interlining
          </label>
        </div>
        <div className="form-group">
          <label>Trim Along Bottom</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
            <input type="checkbox" checked={state.trimAlongBottom}
              onChange={e => s({ trimAlongBottom: e.target.checked })} disabled={disabled} />
            Add bottom trim
          </label>
        </div>
        <TrimNameInput value={state.trimDescription} onChange={(v) => s({ trimDescription: v })} disabled={disabled} />
        <div className="form-group">
          <label>Operating System</label>
          <select className="input" value={state.operatingSystem}
            onChange={e => s({ operatingSystem: e.target.value as OperatingSystem })} disabled={disabled}>
            <option value="manual">Cord &amp; Cleat</option>
            <option value="rollease">Rollease</option>
            <option value="somfy_standard">Somfy Motor</option>
          </select>
        </div>
      </div>
    </div>
  );
});

export default BalloonShadeForm;
